import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { ManagerAssessmentSkillSelectorOptionValue } from '@/constants/review/ManagerAssessmentSkillSelectorOption';
import type { TranslationKey } from '@/i18n-key';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';

import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { ReviewQuestionStatus } from '@/constants/questionnaire/Question';
import { ManagerAssessmentSkillSelectorOption } from '@/constants/review/ManagerAssessmentSkillSelectorOption';

export interface LaunchGuardEmail {
    enabled: boolean;
    isBodyEmpty: boolean;
}

export interface LaunchGuardMilestone {
    id: string;
    labelKey: TranslationKey;
    scheduledAt?: Date;
    dueAt?: Date;
    isClosure?: boolean;
    email?: LaunchGuardEmail;
}

export interface LaunchGuardBlockedMilestone {
    id: string;
    labelKey: TranslationKey;
    missingDate: boolean;
    emptyEnabledBody: boolean;
    afterClosure: boolean;
}

export interface LaunchGuardQuestionnaire {
    id: string;
    labelKey: TranslationKey;
    hasQuestionnaireContent: boolean;
}

export interface LaunchGuardBlockedQuestionnaire {
    id: string;
    labelKey: TranslationKey;
}

export interface LaunchGuardManagerTemplate {
    skillSelectorOption?: ManagerAssessmentSkillSelectorOptionValue | null;
}

export interface LaunchGuardQuestionnaireContent {
    reviewQuestions?: readonly ReviewQuestion[];
    reviewTemplateId?: string | null;
    reviewTemplate?: LaunchGuardManagerTemplate | null;
}

export type ResolveManagerSkillSelectorOptionParams = Pick<
    LaunchGuardQuestionnaireContent,
    'reviewTemplateId' | 'reviewTemplate'
>;

export interface LaunchGuardResult {
    isBlocked: boolean;
    isConfigurationLocked: boolean;
    blockedMilestones: LaunchGuardBlockedMilestone[];
    blockedQuestionnaires: LaunchGuardBlockedQuestionnaire[];
}

export interface EvaluateLaunchGuardParams {
    milestones: readonly LaunchGuardMilestone[];
    questionnaires?: readonly LaunchGuardQuestionnaire[];
    isConfigurationLocked?: boolean;
}

export interface HasQuestionnaireContentParams {
    questionnaire: LaunchGuardQuestionnaireContent | undefined;
    reviewType: EvaluationTypeValue | undefined;
}

const NON_BREAKING_SPACE_ENTITIES = /&nbsp;|&#160;|&#x0*a0;/gi;

export const isEmailBodyEmpty = (value: string): boolean =>
    value
        .replace(/<[^>]*>/g, '')
        .replace(NON_BREAKING_SPACE_ENTITIES, ' ')
        .trim().length === 0;

export const hasActiveReviewQuestions = (
    reviewQuestions: readonly ReviewQuestion[] | undefined,
): boolean =>
    (reviewQuestions ?? []).some((question) => question.status === ReviewQuestionStatus.ACTIVE);

// Competency questions are generated per employee at launch time, never stored beforehand.
export const hasCompetencyQuestions = (
    skillSelectorOption: ManagerAssessmentSkillSelectorOptionValue | null | undefined,
): boolean =>
    skillSelectorOption !== undefined &&
    skillSelectorOption !== null &&
    skillSelectorOption !== ManagerAssessmentSkillSelectorOption.DO_NOT_ASSESS_SKILLS;

// The server resolves the id, not the object: a dangling id (template deleted) yields zero
// competencies, never the "no template at all" fallback to All Skills.
export const resolveManagerSkillSelectorOption = ({
    reviewTemplateId,
    reviewTemplate,
}: ResolveManagerSkillSelectorOptionParams):
    | ManagerAssessmentSkillSelectorOptionValue
    | null
    | undefined => {
    if (reviewTemplate) {
        return reviewTemplate.skillSelectorOption;
    }

    return reviewTemplateId ? undefined : ManagerAssessmentSkillSelectorOption.ALL_SKILLS;
};

export const hasQuestionnaireContent = ({
    questionnaire,
    reviewType,
}: HasQuestionnaireContentParams): boolean =>
    hasActiveReviewQuestions(questionnaire?.reviewQuestions) ||
    (reviewType === EvaluationType.MANAGER_EVALUATION &&
        hasCompetencyQuestions(
            resolveManagerSkillSelectorOption({
                reviewTemplateId: questionnaire?.reviewTemplateId,
                reviewTemplate: questionnaire?.reviewTemplate,
            }),
        ));

const toComparableTime = (date: Date | undefined): number | undefined => {
    const time = date?.getTime();

    return time === undefined || Number.isNaN(time) ? undefined : time;
};

const getLastTime = (milestone: LaunchGuardMilestone): number | undefined =>
    toComparableTime(milestone.dueAt) ?? toComparableTime(milestone.scheduledAt);

const isAfterClosure = ({
    milestone,
    closureTime,
}: {
    milestone: LaunchGuardMilestone;
    closureTime: number | undefined;
}): boolean => {
    if (closureTime === undefined) {
        return false;
    }

    const lastTime = getLastTime(milestone);

    return lastTime !== undefined && lastTime > closureTime;
};

export const evaluateLaunchGuard = ({
    milestones,
    questionnaires = [],
    isConfigurationLocked = false,
}: EvaluateLaunchGuardParams): LaunchGuardResult => {
    const closureTime = toComparableTime(
        milestones.find((milestone) => milestone.isClosure)?.scheduledAt,
    );

    const blockedMilestones: LaunchGuardBlockedMilestone[] = milestones
        .map((milestone) => ({
            milestone,
            missingDate: !milestone.scheduledAt,
            emptyEnabledBody: !!milestone.email?.enabled && milestone.email.isBodyEmpty,
            afterClosure: isAfterClosure({ milestone, closureTime }),
        }))
        .filter(
            ({ missingDate, emptyEnabledBody, afterClosure }) =>
                missingDate || emptyEnabledBody || afterClosure,
        )
        .map(({ milestone, missingDate, emptyEnabledBody, afterClosure }) => ({
            id: milestone.id,
            labelKey: milestone.labelKey,
            missingDate,
            emptyEnabledBody,
            afterClosure,
        }));

    const blockedQuestionnaires: LaunchGuardBlockedQuestionnaire[] = questionnaires
        .filter((questionnaire) => !questionnaire.hasQuestionnaireContent)
        .map((questionnaire) => ({ id: questionnaire.id, labelKey: questionnaire.labelKey }));

    return {
        isBlocked:
            isConfigurationLocked ||
            blockedMilestones.length > 0 ||
            blockedQuestionnaires.length > 0,
        isConfigurationLocked,
        blockedMilestones,
        blockedQuestionnaires,
    };
};
